/**
 * InvoiceForm
 *
 * Orchestrates the decomposed invoice form sub-components.
 * This file only contains wiring/composition logic — all state lives in
 * useInvoiceForm, all effects in useInvoiceFormEffects, all the form payload
 * builder in ./InvoiceForm-buildState.ts, and all rendering in the dedicated
 * section components.
 */

import { type FormEvent, useCallback } from 'react'

import { PageHeaderWithBreadcrumbs } from '@elsapiens/layout'
import { usePageHeader } from '@elsapiens/providers'
import { Link } from 'react-router-dom'

import type { Party } from '@/gen/proto'

import {
  getPageDescription,
  getPageTitle,
  getSubmitButtonLabel,
  partiesToOptions,
  PURCHASE_INVOICE_TYPE_OPTIONS,
  SALES_INVOICE_TYPE_OPTIONS,
} from './invoice-form-helpers'
import { buildFormState } from './InvoiceForm-buildState'
import { FormStyles, PostedInvoiceWarningBanner } from './InvoiceForm-styles'
import { InvoiceFormActions, InvoiceFormPageActions } from './InvoiceFormActions'
import { InvoiceFormAdditional } from './InvoiceFormAdditional'
import { InvoiceFormAddresses } from './InvoiceFormAddresses'
import { InvoiceFormHeader } from './InvoiceFormHeader'
import { InvoiceFormLineItems } from './InvoiceFormLineItems'
import { InvoiceFormModals } from './InvoiceFormModals'
import { InvoiceFormTotals } from './InvoiceFormTotals'
import { useInvoiceForm } from './useInvoiceForm'
import { useInvoiceFormEffects } from './useInvoiceFormEffects'
import { useFiscalPeriodCheck } from '../../../hooks/useFiscalPeriodCheck'
import { createParty, getNextPartyCode } from '../../../services/party-service'
import { FiscalPeriodWarning } from '../../FiscalPeriodWarning'
import { type QuickCustomerData } from '../../party/QuickCustomerForm'

import type { InvoiceFormProps } from './types'

export function InvoiceForm(props: InvoiceFormProps): JSX.Element {
  const {
    initialData, onSubmit, onCancel, parties,
    isLoading = false, isVendor = false, sellerDetails,
    onPartyUpdate, onDownloadPDF, onView,
  } = props

  const form = useInvoiceForm(initialData, parties, isVendor, sellerDetails)
  useInvoiceFormEffects({ form, initialData, sellerDetails, isVendor })

  const invoiceTypeOptions = isVendor ? PURCHASE_INVOICE_TYPE_OPTIONS : SALES_INVOICE_TYPE_OPTIONS
  const partyLabel = isVendor ? 'Supplier' : 'Customer'
  const pageTitle = getPageTitle(form.isEditing, isVendor)

  useInvoicePageHeader(pageTitle, getPageDescription(form.isEditing, isVendor), isVendor)

  const partyOptions = partiesToOptions(parties, form.orgCountryCode, form.invoiceType)
  const formWithPartyOptions = Object.assign(form, { partyOptions })

  const handleQuickCreateCustomer = useQuickCustomerHandler({
    sellerDetails, parties, onPartyUpdate, isVendor,
  })
  const handleSubmit = useSubmitHandler(form, onSubmit)

  return (
    <InvoiceFormShell
      form={form}
      formWithPartyOptions={formWithPartyOptions}
      initialData={initialData}
      invoiceTypeOptions={invoiceTypeOptions}
      isLoading={isLoading}
      isVendor={isVendor}
      partyLabel={partyLabel}
      sellerDetails={sellerDetails}
      onCancel={onCancel}
      onDownloadPDF={onDownloadPDF}
      onPartyUpdate={onPartyUpdate}
      onQuickCreateCustomer={handleQuickCreateCustomer}
      onSubmit={handleSubmit}
      onView={onView}
    />
  )
}

interface InvoiceFormShellProps {
  form: ReturnType<typeof useInvoiceForm>
  formWithPartyOptions: ReturnType<typeof useInvoiceForm>
  initialData: InvoiceFormProps['initialData']
  invoiceTypeOptions: typeof SALES_INVOICE_TYPE_OPTIONS
  isLoading: boolean
  isVendor: boolean
  partyLabel: string
  sellerDetails: InvoiceFormProps['sellerDetails']
  onCancel: InvoiceFormProps['onCancel']
  onDownloadPDF: InvoiceFormProps['onDownloadPDF']
  onPartyUpdate: InvoiceFormProps['onPartyUpdate']
  onQuickCreateCustomer: (data: QuickCustomerData) => Promise<Party>
  onSubmit: (e: FormEvent) => Promise<void>
  onView: InvoiceFormProps['onView']
}

function InvoiceFormShell(props: InvoiceFormShellProps): JSX.Element {
  const {
    form, formWithPartyOptions, initialData, invoiceTypeOptions, isLoading, isVendor,
    partyLabel, sellerDetails, onCancel, onDownloadPDF, onPartyUpdate,
    onQuickCreateCustomer, onSubmit, onView,
  } = props
  const { warning: periodWarning } = useFiscalPeriodCheck(form.invoiceDate, sellerDetails.orgId)
  return (
    <div className="flex flex-col">
      <InvoiceFormPageHeader
        form={form}
        initialData={initialData}
        sellerDetails={sellerDetails}
        onCancel={onCancel}
        onDownloadPDF={onDownloadPDF}
        onView={onView}
      />
      <div className="el-p-lg">
        <form className="flex flex-col el-gap-xs" onSubmit={(e) => { void onSubmit(e) }}>
          {form.isEditingPosted && <PostedInvoiceWarningBanner />}
          <FiscalPeriodWarning message={periodWarning} />
          <InvoiceFormBody
            form={formWithPartyOptions}
            invoiceTypeOptions={invoiceTypeOptions}
            isLoading={isLoading}
            isVendor={isVendor}
            partyLabel={partyLabel}
            onCancel={onCancel}
          />
          <FormStyles />
        </form>
        <InvoiceFormModals
          form={form}
          initialDataId={initialData?.id}
          isVendor={isVendor}
          sellerDetails={sellerDetails}
          onPartyCreated={(newParty) => form.setSelectedPartyId(newParty.id)}
          onPartyUpdate={onPartyUpdate}
          onQuickCreateCustomer={onQuickCreateCustomer}
        />
      </div>
    </div>
  )
}

export default InvoiceForm

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface InvoiceFormPageHeaderProps {
  initialData: InvoiceFormProps['initialData']
  form: ReturnType<typeof useInvoiceForm>
  sellerDetails: InvoiceFormProps['sellerDetails']
  onCancel: InvoiceFormProps['onCancel']
  onDownloadPDF: InvoiceFormProps['onDownloadPDF']
  onView: InvoiceFormProps['onView']
}

function InvoiceFormPageHeader(props: InvoiceFormPageHeaderProps): JSX.Element {
  const { initialData, form, sellerDetails, onCancel, onDownloadPDF, onView } = props
  return (
    <PageHeaderWithBreadcrumbs
      description={initialData?.id ? 'Update invoice details' : 'Create a new invoice for your customer'}
      linkComponent={Link}
      title={initialData?.id ? 'Edit Invoice' : 'New Invoice'}
      variant="card"
    >
      <InvoiceFormPageActions
        initialDataId={initialData?.id}
        isSubmitting={form.isSubmitting}
        sellerOrgId={sellerDetails.orgId}
        onCancel={onCancel}
        onDownloadPDF={onDownloadPDF}
        onShowVersionHistory={() => form.setIsVersionHistoryVisible(true)}
        onView={onView}
      />
    </PageHeaderWithBreadcrumbs>
  )
}

interface InvoiceFormBodyProps {
  form: ReturnType<typeof useInvoiceForm>
  invoiceTypeOptions: typeof SALES_INVOICE_TYPE_OPTIONS
  isLoading: boolean
  isVendor: boolean
  partyLabel: string
  onCancel: InvoiceFormProps['onCancel']
}

function InvoiceFormBody(props: InvoiceFormBodyProps): JSX.Element {
  const { form, invoiceTypeOptions, isLoading, isVendor, partyLabel, onCancel } = props
  return (
    <>
      <InvoiceFormHeader
        form={form}
        invoiceTypeOptions={invoiceTypeOptions}
        isLoading={isLoading}
        isVendor={isVendor}
        partyLabel={partyLabel}
        onOpenQuickCreate={() => form.setIsQuickCustomerFormOpen(true)}
      />
      <InvoiceFormAddresses
        form={form}
        isLoading={isLoading}
        isVendor={isVendor}
        partyLabel={partyLabel}
        onOpenBillingSelector={() => form.setIsBillingAddressSelectorOpen(true)}
        onOpenShippingSelector={() => form.setIsShippingAddressSelectorOpen(true)}
      />
      <InvoiceFormLineItems form={form} isLoading={isLoading} />
      <InvoiceFormTotals form={form} />
      <InvoiceFormAdditional form={form} isLoading={isLoading} isVendor={isVendor} />
      <InvoiceFormActions
        form={form}
        isLoading={isLoading}
        submitButtonLabel={getSubmitButtonLabel(form.isSubmitting, form.isEditing)}
        onCancel={onCancel}
      />
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Hooks
// ─────────────────────────────────────────────────────────────────────────────

function useInvoicePageHeader(pageTitle: string, description: string, isVendor: boolean): void {
  usePageHeader({
    title: pageTitle,
    description,
    breadcrumbs: [
      { label: 'Home', href: '/', as: Link },
      {
        label: isVendor ? 'Vendor Bills' : 'Invoices',
        href: isVendor ? '/bills' : '/invoices',
        as: Link,
      },
      { label: pageTitle },
    ],
  })
}

interface UseQuickCustomerHandlerArgs {
  sellerDetails: InvoiceFormProps['sellerDetails']
  parties: InvoiceFormProps['parties']
  onPartyUpdate: InvoiceFormProps['onPartyUpdate']
  isVendor: boolean
}

function buildQuickCustomerRequest(args: {
  orgId: string
  ledgerId: string
  nextCode: string
  data: QuickCustomerData
  isVendor: boolean
}): Parameters<typeof createParty>[2] {
  const { orgId, ledgerId, nextCode, data, isVendor } = args
  return {
    orgId, ledgerId, code: nextCode, name: data.name,
    partyType: isVendor ? 'PARTY_TYPE_VENDOR' : 'PARTY_TYPE_CUSTOMER',
    gstRegistrationType: data.gstin ? 'GST_REGISTRATION_TYPE_REGULAR' : 'GST_REGISTRATION_TYPE_UNREGISTERED',
    gstin: data.gstin ?? '',
    companyVendorCode: data.companyVendorCode ?? '',
    // Currency is mandatory server-side — always send it (the quick form
    // defaults it to the base currency).
    defaultCurrency: data.defaultCurrency,
    contact: { email: data.email ?? '', phone: data.phone ?? '', mobile: '', website: '' },
  } as unknown as Parameters<typeof createParty>[2]
}

function useQuickCustomerHandler(
  args: UseQuickCustomerHandlerArgs,
): (data: QuickCustomerData) => Promise<Party> {
  const { sellerDetails, parties, onPartyUpdate, isVendor } = args
  return useCallback(async (data: QuickCustomerData): Promise<Party> => {
    if (!sellerDetails?.orgId || !sellerDetails?.ledgerId) {
      throw new Error('Organization or Ledger not available')
    }
    const nextCode = getNextPartyCode(isVendor ? ['vendor'] : ['customer'], parties.map((p) => p.code))
    const createRequest = buildQuickCustomerRequest({
      orgId: sellerDetails.orgId, ledgerId: sellerDetails.ledgerId,
      nextCode, data, isVendor,
    })
    const response = await createParty(sellerDetails.orgId, sellerDetails.ledgerId, createRequest)
    if (!response.data) { throw new Error(`Failed to create ${isVendor ? 'supplier' : 'customer'}`) }
    onPartyUpdate?.()
    return response.data
  }, [sellerDetails, parties, onPartyUpdate, isVendor])
}

function useSubmitHandler(
  form: ReturnType<typeof useInvoiceForm>,
  onSubmit: InvoiceFormProps['onSubmit'],
): (e: FormEvent) => Promise<void> {
  return useCallback(async (e: FormEvent) => {
    e.preventDefault()
    if (!form.validate()) { return }
    form.setIsSubmitting(true)
    try {
      const formState = buildFormState(form)
      await onSubmit(formState)
    } catch {
      form.setErrors({ submit: 'Failed to save invoice. Please try again.' })
    } finally {
      form.setIsSubmitting(false)
    }
   
  }, [form, onSubmit])
}
